import { Input, Checkbox, Modal } from 'antd';
import React from 'react';
import ewtStore from '../EWTStore';
import { theme, ewtModalClass, modalStyle, formatPlayerHandicap } from '../theme';

const clonePlayer = (player) =>
    player ? JSON.parse(JSON.stringify(player)) : null;

const isValidTournamentId = (id) => /^\d+$/.test(String(id));

const normalizeTournamentIds = (tournaments) => {
    if (!tournaments) return [];
    let ids = tournaments;
    if (typeof tournaments === 'string') {
        ids = tournaments.split(',').map((id) => id.trim()).filter(Boolean);
    }
    if (!Array.isArray(ids)) return [];
    return ids.filter(isValidTournamentId);
};

const getTournamentRows = (player) => {
    const ids = new Set([
        ...normalizeTournamentIds(player.tournaments),
        ...Object.keys(player.handicaps || {}).filter(isValidTournamentId),
    ]);
    return Array.from(ids).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10)
    );
};

const buildPlayerState = (player) => {
    if (!player) return null;
    const tournamentHandicaps = {};
    getTournamentRows(player).forEach((tournamentID) => {
        if (player.handicaps && player.handicaps[tournamentID] !== undefined) {
            tournamentHandicaps[tournamentID] = player.handicaps[tournamentID];
        } else {
            tournamentHandicaps[tournamentID] = '';
        }
    });
    return { ...player, tournamentHandicaps };
};

const LabeledInput = ({ label, value, onChange }) => (
    <Input
        className="ewtFixedLabelInput"
        addonBefore={label}
        value={value}
        onChange={onChange}
    />
);

const HandicapsSection = ({ player, readOnly, setHandicap, fullHeight }) => (
    <div
        className="flexColumn"
        style={{
            marginTop: 4,
            flex: fullHeight ? 1 : undefined,
            minHeight: fullHeight ? 0 : undefined,
        }}
    >
        <div style={{ fontWeight: 600, marginBottom: 6, color: theme.navy }}>
            Tournament Handicaps
        </div>
        <div
            className="flexColumn flexScroll"
            style={{
                flex: fullHeight ? 1 : undefined,
                maxHeight: fullHeight ? undefined : 160,
                minHeight: fullHeight ? 0 : undefined,
                overflowY: 'auto',
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: '8px 10px',
                backgroundColor: theme.surfaceAlt,
            }}
        >
            <div className="flexRow" style={{ color: theme.slate, fontSize: theme.fontSm, marginBottom: 4 }}>
                <div style={{ width: 50 }}>Year</div>
                <div style={{ width: 120 }}>Handicap</div>
            </div>
            {getTournamentRows(player).length === 0 && (
                <div style={{ color: theme.slate, fontSize: theme.fontSm }}>
                    No tournaments yet. Add the player to a group first.
                </div>
            )}
            {getTournamentRows(player).map((tournamentID) => (
                <div
                    key={tournamentID}
                    className="flexRow"
                    style={{ alignItems: 'center', marginTop: 4 }}
                >
                    <div style={{ width: 50, color: theme.navy }}>{tournamentID}</div>
                    <div style={{ width: 120 }}>
                        {readOnly ? (
                            player.tournamentHandicaps &&
                            player.tournamentHandicaps[tournamentID] != null
                                ? formatPlayerHandicap(
                                      player.tournamentHandicaps[tournamentID]
                                  )
                                : ''
                        ) : (
                            <Input
                                size="small"
                                value={
                                    player.tournamentHandicaps &&
                                    player.tournamentHandicaps[tournamentID] != null
                                        ? player.tournamentHandicaps[tournamentID]
                                        : ''
                                }
                                onChange={(e) => setHandicap(tournamentID, e.target.value)}
                                placeholder="Handicap"
                            />
                        )}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export const PlayerDetails = (props) => {
    const readOnly = props.readOnly || !props.savePlayer;
    const [player, setPlayer] = React.useState(() =>
        buildPlayerState(clonePlayer(props.player))
    );

    React.useEffect(() => {
        if (props.player) {
            setPlayer(buildPlayerState(clonePlayer(props.player)));
        }
    }, [props.player, props.open]);

    if (!props.player) return null;

    const setProperty = (propertyName, value) => {
        setPlayer((prev) => ({ ...prev, [propertyName]: value }));
    };

    const setHandicap = (tournamentID, value) => {
        setPlayer((prev) => ({
            ...prev,
            tournamentHandicaps: {
                ...(prev.tournamentHandicaps || {}),
                [tournamentID]: value,
            },
        }));
    };

    const save = async () => {
        if (!props.savePlayer) return;
        await props.savePlayer(player);
    };

    const isModal = props.open != null;
    const modalBodyHeight = ewtStore.windowHeight - 160;

    const formContent = (
        <div
            className="flexColumn"
            style={{
                gap: 10,
                fontSize: 16,
                height: isModal ? '100%' : undefined,
                minHeight: isModal ? 0 : undefined,
                flex: isModal ? 1 : undefined,
            }}
        >
            {player.id && (
                <div style={{ color: theme.slate, fontSize: theme.fontSm }}>
                    Player ID&nbsp;{player.id}
                </div>
            )}

            {readOnly ? (
                <>
                    <div>Player Code: {player.code || ''}</div>
                    <div>Last Name: {player.last_name}</div>
                    <div>First Name: {player.first_name}</div>
                    <div>Nickname: {player.nickname || ''}</div>
                    <div>Active: {player.active ? 'Yes' : 'No'}</div>
                    {player.admin && <div>Administrator</div>}
                </>
            ) : (
                <>
                    <LabeledInput
                        label="Code"
                        value={player.code || ''}
                        onChange={(e) => setProperty('code', e.target.value)}
                    />
                    <LabeledInput
                        label="Last Name"
                        value={player.last_name || ''}
                        onChange={(e) => setProperty('last_name', e.target.value)}
                    />
                    <LabeledInput
                        label="First Name"
                        value={player.first_name || ''}
                        onChange={(e) => setProperty('first_name', e.target.value)}
                    />
                    <LabeledInput
                        label="Nickname"
                        value={player.nickname || ''}
                        onChange={(e) => setProperty('nickname', e.target.value)}
                    />
                    <div className="flexRow" style={{ gap: 16 }}>
                        <Checkbox
                            checked={!!player.active}
                            onChange={(e) => setProperty('active', e.target.checked)}
                        >
                            Active
                        </Checkbox>
                        <Checkbox
                            checked={!!player.admin}
                            onChange={(e) => setProperty('admin', e.target.checked)}
                        >
                            Administrator
                        </Checkbox>
                    </div>
                </>
            )}

            <HandicapsSection
                player={player}
                readOnly={readOnly}
                setHandicap={setHandicap}
                fullHeight={isModal}
            />
        </div>
    );

    if (isModal) {
        return (
            <Modal
                className={ewtModalClass}
                title={player.id ? 'Edit Player' : 'Add Player'}
                open={props.open}
                onOk={save}
                onCancel={props.hideModal || props.onCancel}
                okText="Save"
                cancelText="Cancel"
                width={380}
                zIndex={2000}
                destroyOnClose
                style={modalStyle}
                bodyStyle={{
                    margin: 0,
                    padding: 16,
                    height: modalBodyHeight,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {formContent}
            </Modal>
        );
    }

    return formContent;
};
